import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { BankBox } from "../components/BankBox";
import {
  CASE_STATUSES,
  CASE_STATUS_LABELS,
  CREDIT_RATES,
  PRICING,
  type CaseStatus,
} from "../../shared/constants";

interface CaseItem {
  id: string;
  slug: string | null;
  status: string;
  profession: string;
  office_name: string | null;
  published_url: string | null;
  updated_at: string;
}
interface LedgerEntry {
  id: string;
  entry_type: "grant" | "consume" | "adjust" | "expire" | "pending";
  delta: number;
  reason: string | null;
  created_at: string;
}
interface Invoice {
  id: string;
  kind: "initial" | "monthly" | "credit_charge";
  amount_total: number;
  status: "unpaid" | "paid" | "canceled";
  created_at: string;
}
interface Activity {
  id: string;
  action: string;
  detail: string | null;
  meta: string | null;
  created_at: string;
}
interface Analytics {
  site_views: number;
  blog_views: number;
  days: number;
}
interface OsSummary {
  keywords: Array<{ id: string; keyword: string; fixed_status: string; target_page_id: string | null }>;
  pages: Array<{ id: string; title: string; status: string; missing_assets: string[] }>;
  recommendations: Array<{ id: string; title: string; description: string; impact_score: number; status: string }>;
  personas: Array<{ id: string; name: string; role: string }>;
}

const yen = (n: number) => `${n.toLocaleString("ja-JP")}円`;
const jstDate = (s: string) => new Date(s).toLocaleString("ja-JP", { dateStyle: "medium", timeStyle: "short" });
const ENTRY_LABEL: Record<LedgerEntry["entry_type"], string> = {
  grant: "付与",
  consume: "消費",
  adjust: "調整",
  expire: "期限切れ",
  pending: "確認待ち",
};

function activityMeta(meta: string | null): { url?: string; label?: string } {
  if (!meta) return {};
  try {
    const parsed = JSON.parse(meta) as { url?: string; label?: string };
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// No.11 公開後ダッシュボード・クレジット管理。
export function DashboardPage() {
  const { user } = useAuth();
  const [caseRow, setCaseRow] = useState<CaseItem | null>(null);
  const [balance, setBalance] = useState(0);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [os, setOs] = useState<OsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [chargeInvoice, setChargeInvoice] = useState<Invoice | null>(null);
  const [charging, setCharging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [revisionMessage, setRevisionMessage] = useState("");
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [creditVisible, setCreditVisible] = useState(10);
  const [activityVisible, setActivityVisible] = useState(10);

  useEffect(() => {
    void (async () => {
      try {
        const [c, h, iv, ac] = await Promise.all([
          api.get<{ case: CaseItem | null }>("/api/cases/current"),
          api.get<{ balance: number; entries: LedgerEntry[] }>("/api/cases/credits/history"),
          api.get<{ invoices: Invoice[] }>("/api/cases/invoices"),
          api.get<{ activity: Activity[] }>("/api/cases/activity"),
        ]);
        setCaseRow(c.case);
        setBalance(h.balance);
        setEntries(h.entries);
        setInvoices(iv.invoices);
        setActivity(ac.activity);
        if (c.case) {
          try {
            const [an, osRes] = await Promise.all([
              api.get<{ analytics: Analytics }>(`/api/cases/${c.case.id}/analytics`),
              api.get<{ os: OsSummary }>(`/api/cases/${c.case.id}/os`),
            ]);
            setAnalytics(an.analytics);
            setOs(osRes.os);
          } catch {
            /* アクセス解析の取得失敗はダッシュボード全体を止めない */
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshCredits = async () => {
    const h = await api.get<{ balance: number; entries: LedgerEntry[] }>(
      "/api/cases/credits/history",
    );
    setBalance(h.balance);
    setEntries(h.entries);
    const ac = await api.get<{ activity: Activity[] }>("/api/cases/activity");
    setActivity(ac.activity);
  };

  const submitRevision = async () => {
    if (!caseRow) return;
    if (!revisionMessage.trim()) {
      setError("修正内容を入力してください");
      return;
    }
    setError(null);
    setNotice(null);
    setSubmittingRevision(true);
    try {
      const res = await api.post<{ balance: number; cost: number }>(
        `/api/cases/${caseRow.id}/revision`,
        { message: revisionMessage.trim() },
      );
      setBalance(res.balance);
      setRevisionMessage("");
      setNotice(`修正依頼を受け付けました（-${res.cost}cr）。運営が対応後、再デプロイします。`);
      await refreshCredits();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "修正依頼に失敗しました");
    } finally {
      setSubmittingRevision(false);
    }
  };

  const charge = async (amount: number) => {
    setError(null);
    setCharging(amount);
    try {
      const res = await api.post<{ invoice: Invoice }>("/api/cases/credits/charge", { amount });
      setChargeInvoice(res.invoice);
      const iv = await api.get<{ invoices: Invoice[] }>("/api/cases/invoices");
      setInvoices(iv.invoices);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "チャージ申込に失敗しました");
    } finally {
      setCharging(null);
    }
  };

  if (loading) return <div className="page-center">読み込み中...</div>;

  const status = (caseRow?.status ?? "intake") as CaseStatus;
  const statusIndex = CASE_STATUSES.indexOf(status);
  const safeStatusIndex = statusIndex >= 0 ? statusIndex : 0;
  const progressPct = Math.round(((safeStatusIndex + 1) / CASE_STATUSES.length) * 100);
  const isPublished = status === "published";
  const unpaidInitial = invoices.find((i) => i.kind === "initial" && i.status === "unpaid");
  const completedSteps = Math.min(safeStatusIndex + 1, 5);
  const keywordCount = os?.keywords.length ?? 0;
  const pageCount = os?.pages.length ?? 0;
  const approvedPageCount = os?.pages.filter((p) => p.status === "approved").length ?? 0;
  const missingAssetLabels = Array.from(new Set((os?.pages ?? []).flatMap((p) => p.missing_assets ?? []))).slice(0, 6);
  const realCheckItems = [
    { label: "キーワード戦略の確認", done: keywordCount > 0, to: "/keywords" },
    { label: "不足素材の確認", done: missingAssetLabels.length === 0 && pageCount > 0, to: "/assets" },
    { label: "ページ設計の承認", done: pageCount > 0 && approvedPageCount > 0, to: "/page-plan" },
  ];
  const checkItems = [
    ...realCheckItems,
  ];
  const siteViews = analytics?.site_views ?? 0;
  const hasRankingData = false;

  return (
    <section className="lp-page">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="lp-page-head">
        <div>
          <h1>こんにちは、{user?.last_name || "お客様"}さん</h1>
          <p>サイトの作成・運用の進捗をやさしくお知らせします。</p>
        </div>
        <div className="lp-date-chip">2026/07/01 - 2026/07/31</div>
      </div>

      <div className="lp-action-banner">
        <div>
          <span className="lp-section-kicker">今日のおすすめアクション</span>
          <h2>{checkItems.filter((i) => !i.done).length}件の内容を確認しましょう</h2>
          <p>確認が必要な項目があります。5分ほどで完了します。</p>
        </div>
        <Link to="/hearing" className="btn btn-primary btn-lg">
          確認する →
        </Link>
      </div>

      {unpaidInitial && (
        <div className="lp-warning-card">
          <strong>初期費用のお支払い待ちです</strong>
          <p>
            初期費用 {yen(unpaidInitial.amount_total)} のお支払いを確認後、制作を開始します。
            登録時の特典として{PRICING.initialCreditGrant.toLocaleString("ja-JP")}クレジットも付与されています。
          </p>
          <BankBox />
        </div>
      )}

      <div className="lp-dashboard-grid">
        <div className="lp-card lp-progress-card">
          <h2>サイトの進捗</h2>
          <div className="lp-progress-summary">
            <div className="lp-ring" style={{ ["--p" as string]: `${progressPct * 3.6}deg` }}>
              <span>{progressPct}%</span>
            </div>
            <div>
              <strong>{isPublished ? "サイトは公開中です" : "公開まであと少しです"}</strong>
              <p>全5ステップ中、{completedSteps}ステップが完了しました。</p>
              <span className="lp-status-pill">{CASE_STATUS_LABELS[status]}</span>
            </div>
          </div>
          <div className="lp-stepper">
            {["基本情報", "対応業務", "ページ設計", "実装", "公開・確認"].map((label, i) => (
              <div key={label} className={i < completedSteps ? "done" : i === completedSteps ? "current" : ""}>
                <span>{i < completedSteps ? "✓" : i + 1}</span>
                <small>{label}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-card">
          <h2>最近の活動</h2>
          <div className="lp-activity-list">
            {activity.length === 0 && <p className="muted">まだ活動履歴はありません。</p>}
            {activity.slice(0, 3).map((a) => (
              <div key={a.id} className="lp-activity-item">
                <span className="lp-dot" />
                <div>
                  <strong>{a.detail ?? a.action}</strong>
                  {activityMeta(a.meta).url && (
                    <a className="lp-inline-link" href={activityMeta(a.meta).url}>
                      {activityMeta(a.meta).label ?? "開く"}
                    </a>
                  )}
                  <small>{jstDate(a.created_at)}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lp-metric-grid">
        <div className="lp-card lp-metric">
          <span>狙うキーワード</span>
          <strong>{keywordCount.toLocaleString("ja-JP")}</strong>
          <small>実際に登録済みの数</small>
        </div>
        <div className="lp-card lp-metric">
          <span>上位表示</span>
          <strong>{hasRankingData ? "0" : "未計測"}</strong>
          <small>Search Console連携後に表示</small>
        </div>
        <div className="lp-card lp-metric">
          <span>サイト訪問数</span>
          <strong>{siteViews.toLocaleString("ja-JP")}</strong>
          <small>直近30日</small>
        </div>
        <div className="lp-card lp-metric">
          <span>残りクレジット</span>
          <strong>{balance.toLocaleString("ja-JP")}</strong>
          <small>1cr = 1円</small>
        </div>
      </div>

      <div className="lp-dashboard-grid">
        <div className="lp-card">
          <h2>確認が必要な項目</h2>
          <div className="lp-check-list">
            {checkItems.map((item) => (
              <div key={item.label}>
                <span className={item.done ? "lp-check done" : "lp-check warn"}>
                  {item.done ? "✓" : "!"}
                </span>
                <strong>{item.label}</strong>
                <Link to={item.to}>確認する</Link>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-card lp-advice-card">
          <span className="lp-section-kicker">ワンポイントアドバイス</span>
          <p>{keywordCount === 0
            ? "まずヒアリングを完了し、実際に狙うキーワードを作成してください。"
            : pageCount === 0
              ? "採用キーワードをもとにページ設計を作成してください。"
              : isPublished
                ? "公開後のアクセスや問い合わせイベントを見ながら改善します。"
                : "ページ設計を確認し、制作に進める状態にしましょう。"}</p>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDetailsOpen(true)}>
            詳細に見る
          </button>
        </div>
      </div>

      {detailsOpen && (
        <div className="lp-modal-backdrop" role="presentation" onMouseDown={() => setDetailsOpen(false)}>
          <div className="lp-modal lp-modal-wide" role="dialog" aria-modal="true" aria-labelledby="dashboard-details-title" onMouseDown={(e) => e.stopPropagation()}>
            <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setDetailsOpen(false)}>×</button>
            <span className="lp-section-kicker">REAL DATA ANALYSIS</span>
            <h2 id="dashboard-details-title">現在の詳細分析</h2>
            <div className="lp-detail-grid">
              <div>
                <h3>制作状況</h3>
                <p>ステータス: {CASE_STATUS_LABELS[status] ?? status}</p>
                <p>設計済みページ: {pageCount}件 / 承認済み: {approvedPageCount}件</p>
                <p>公開状態: {isPublished ? "公開済み" : "未公開"}</p>
              </div>
              <div>
                <h3>検索戦略</h3>
                <p>登録キーワード: {keywordCount}件</p>
                <p>上位表示データ: Search Console未連携のため未計測</p>
                <p>検索流入評価: 公開後の実測データが必要です</p>
              </div>
              <div>
                <h3>アクセス</h3>
                <p>サイト訪問数: {siteViews}件 / 直近{analytics?.days ?? 30}日</p>
                <p>ブログ閲覧数: {(analytics?.blog_views ?? 0).toLocaleString("ja-JP")}件</p>
                <p>CVR: 問い合わせイベント計測後に表示</p>
              </div>
              <div>
                <h3>不足素材</h3>
                {missingAssetLabels.length === 0 ? (
                  <p>不足素材は登録されていません。</p>
                ) : (
                  <ul>{missingAssetLabels.map((label) => <li key={label}>{label}</li>)}</ul>
                )}
              </div>
            </div>
            <div className="lp-modal-actions">
              <Link className="btn btn-primary" to="/keywords" onClick={() => setDetailsOpen(false)}>キーワードを確認</Link>
            </div>
          </div>
        </div>
      )}

      {/* サイト修正依頼(公開後のみ) */}
      {isPublished && (
        <div className="lp-card revision-form" id="revision">
          <h2>サイトの修正を依頼する</h2>
          <p className="muted">
            公開中サイトの文言・画像・レイアウトなどの修正を運営に依頼できます。1件につき
            <strong>{CREDIT_RATES.revisionRequest}クレジット</strong>を消費します（残高{" "}
            {balance.toLocaleString("ja-JP")}cr）。ご依頼後、運営が対応し再デプロイします。
          </p>
          <textarea
            value={revisionMessage}
            onChange={(e) => setRevisionMessage(e.target.value)}
            placeholder="例）トップページのキャッチコピーを『相続のご相談は初回無料』に変更してください。"
            rows={4}
          />
          <div className="wizard-actions">
            <button
              className="btn btn-primary"
              onClick={submitRevision}
              disabled={submittingRevision || balance < CREDIT_RATES.revisionRequest}
            >
              {submittingRevision
                ? "送信中..."
                : `${CREDIT_RATES.revisionRequest}crで修正を依頼する`}
            </button>
          </div>
          {balance < CREDIT_RATES.revisionRequest && (
            <p className="muted small">
              クレジットが不足しています。下の「クレジットの追加方法」からチャージしてください。
            </p>
          )}
        </div>
      )}

      {/* クレジットチャージ案内 */}
      <div className="lp-card" id="charge">
        <h2>クレジットの追加方法（銀行振込）</h2>
        <p className="muted">
          クレジットは銀行振込で追加できます。金額を選んでお申し込みいただくと請求が作成されます。入金確認後、運営がクレジットを反映します（1cr=1円・有効期限は付与から{PRICING.creditExpiryMonths}ヶ月）。
        </p>
        <div className="charge-options">
          {PRICING.creditChargeOptions.map((amount) => (
            <button
              key={amount}
              className="btn btn-ghost charge-btn"
              onClick={() => charge(amount)}
              disabled={charging !== null}
            >
              {charging === amount ? "申込中..." : `${yen(amount)}分をチャージ`}
            </button>
          ))}
        </div>

        {chargeInvoice && (
          <div className="pay-inline">
            <p>
              <strong>{yen(chargeInvoice.amount_total)}</strong> のチャージを受け付けました。下記口座へお振込みください。入金確認後にクレジットが反映されます。
            </p>
            <BankBox />
            <p className="muted small">
              ※振込手数料はお客様のご負担となります。反映は運営の手動確認後となります（通知はマイページに表示されます）。
            </p>
          </div>
        )}
        {!chargeInvoice && (
          <>
            <BankBox />
            <p className="muted small">※振込手数料はお客様のご負担となります。</p>
          </>
        )}
      </div>

      {/* クレジット履歴 */}
      <div className="lp-card lp-table-card">
        <h2>クレジット履歴</h2>
        {entries.length === 0 ? (
          <p className="muted">履歴がありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>日時</th>
                <th>種別</th>
                <th>増減</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, creditVisible).map((e) => (
                <tr key={e.id}>
                  <td>{jstDate(e.created_at)}</td>
                  <td>{ENTRY_LABEL[e.entry_type]}</td>
                  <td className={e.entry_type === "pending" ? "" : e.delta >= 0 ? "delta-pos" : "delta-neg"}>
                    {e.entry_type === "pending" ? "反映待ち " : e.delta >= 0 ? "+" : ""}
                    {e.delta.toLocaleString("ja-JP")}cr
                  </td>
                  <td>{e.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {entries.length > creditVisible && (
          <div className="lp-more-row">
            <button className="btn btn-ghost btn-sm" onClick={() => setCreditVisible((n) => n + 10)}>
              もっと見る
            </button>
          </div>
        )}
      </div>

      {/* 最近のアクティビティ */}
      <div className="lp-card lp-table-card">
        <h2>最近のアクティビティ</h2>
        {activity.length === 0 ? (
          <p className="muted">アクティビティがありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>日時</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              {activity.slice(0, activityVisible).map((a) => (
                <tr key={a.id}>
                  <td>{jstDate(a.created_at)}</td>
                  <td>{a.detail ?? a.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {activity.length > activityVisible && (
          <div className="lp-more-row">
            <button className="btn btn-ghost btn-sm" onClick={() => setActivityVisible((n) => n + 10)}>
              もっと見る
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
